import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, X, RotateCcw, PenLine, Trophy, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { MarkDialog } from "@/components/MarkAnswer";

const Card = ({ c, flipped, setFlipped }) => (
  <button data-testid="flashcard" onClick={() => setFlipped(!flipped)} className="flip block w-full text-left" aria-label="Flip card">
    <div className={`flip-inner relative min-h-[280px] sm:min-h-[320px] ${flipped ? "flipped" : ""}`}>
      <div className="flip-face absolute inset-0 card p-6 sm:p-8 flex flex-col">
        <p className="eyebrow">{c.topic || "Question"}</p>
        <p data-testid="flashcard-question" className="mt-4 flex-1 font-display text-2xl sm:text-3xl text-white leading-snug">{c.q}</p>
        <p className="text-xs text-slate-500">Tap to see the answer</p>
      </div>
      <div className="flip-face flip-back absolute inset-0 rounded-3xl border border-gold-400/50 bg-gradient-to-b from-navy-200 to-surface p-6 sm:p-8 flex flex-col shadow-gold">
        <p className="eyebrow">Answer</p>
        <p data-testid="flashcard-answer" className="mt-4 flex-1 text-base sm:text-lg text-white leading-relaxed overflow-y-auto">{c.a}</p>
        <p className="text-xs text-slate-500">Did you get it?</p>
      </div>
    </div>
  </button>
);

const Done = ({ score, total, data, group, onAgain }) => {
  const pct = total ? Math.round((100 * score) / total) : 0;
  return (
    <section data-testid="quiz-done" className="card p-8 text-center animate-rise">
      <Trophy className="w-10 h-10 mx-auto text-gold-300" />
      <p data-testid="quiz-score" className="mt-3 font-display text-6xl text-white">{pct}%</p>
      <p className="mt-1 text-slate-600">{score} of {total} right first time · {data.known} of {data.total} cards learnt</p>
      {group && <p className="mt-2 text-sm text-gold-300">Your score is on the group leaderboard.</p>}
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        <button data-testid="quiz-again" onClick={onAgain} className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-3 text-sm font-bold text-black hover:bg-gold-300"><RotateCcw className="w-4 h-4" />Go again</button>
        <Link to={group ? `/groups/${group}?tab=revise` : `/revise/${data.pack_id}`} data-testid="quiz-exit" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:text-white">{group ? "Back to group" : "Back to pack"}</Link>
      </div>
    </section>
  );
};

export default function Quiz() {
  const { packId } = useParams();
  const [params] = useSearchParams();
  const group = params.get("group");
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(group ? "all" : "due");
  const [queue, setQueue] = useState([]);
  const [flipped, setFlipped] = useState(false);
  const [first, setFirst] = useState({});
  const [mark, setMark] = useState(false);
  const [finished, setFinished] = useState(false);
  const start = (d, m) => {
    const deck = m === "due" && d.due.length ? d.due : d.all;
    setQueue(deck.map((c) => ({ ...c, tries: 0 }))); setFirst({}); setFlipped(false); setFinished(false);
  };
  const load = (m = mode) => api.get(`/revision/packs/${packId}/quiz`).then((r) => { setData(r.data ?? false); if (r.data) start(r.data, m); }).catch(() => setData(false));
  useEffect(() => { load(); }, [packId]); // eslint-disable-line react-hooks/exhaustive-deps
  const deckSize = useMemo(() => new Set([...queue.map((c) => c.idx), ...Object.keys(first).map(Number)]).size, [queue, first]);

  const answer = async (correct) => {
    const c = queue[0];
    const firstTime = !(c.idx in first);
    const nf = firstTime ? { ...first, [c.idx]: correct } : first;
    setFirst(nf);
    api.post(`/revision/packs/${packId}/quiz`, { idx: c.idx, correct }).catch(() => {});
    let rest = queue.slice(1);
    if (!correct && c.tries < 3) { const at = Math.min(rest.length, 3); rest = [...rest.slice(0, at), { ...c, tries: c.tries + 1 }, ...rest.slice(at)]; }
    setFlipped(false);
    setQueue(rest);
    if (!rest.length) {
      const total = Object.keys(nf).length;
      const score = Object.values(nf).filter(Boolean).length;
      if (group && total) await api.post(`/groups/${group}/packs/${packId}/score`, { score, total }).catch(() => {});
      setFinished(true);
      api.get(`/revision/packs/${packId}/quiz`).then((r) => r.data && setData(r.data)).catch(() => {});
    }
  };

  if (data === false) return <main className="mx-auto max-w-2xl px-4 py-12 text-slate-500">This quiz isn't available.</main>;
  if (!data) return <main className="mx-auto max-w-2xl px-4 py-12"><div className="h-72 rounded-3xl bg-surface animate-pulse" /></main>;
  const c = queue[0];
  const score = Object.values(first).filter(Boolean).length;
  return (
    <main data-testid="quiz-page" className="mx-auto max-w-2xl px-4 sm:px-6 py-6 sm:py-10 space-y-5">
      <Link to={group ? `/groups/${group}?tab=revise` : `/revise/${packId}`} data-testid="quiz-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-white"><ArrowLeft className="w-4 h-4" />Back</Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0"><p className="eyebrow">Quiz mode{group ? " · group" : ""}</p><h1 className="mt-1 font-display text-3xl sm:text-4xl text-white leading-tight">{data.title}</h1></div>
        {!group && (
          <div data-testid="quiz-mode" className="inline-flex rounded-full bg-slate-100 p-0.5">
            {[["due", `Due (${data.due.length})`], ["all", `All (${data.total})`]].map(([k, l]) => (
              <button key={k} data-testid={`quiz-mode-${k}`} onClick={() => { setMode(k); start(data, k); }} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${mode === k ? "bg-gold-400 text-black" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
        )}
      </div>
      {finished || !c ? <Done score={score} total={Object.keys(first).length} data={data} group={group} onAgain={() => start(data, "all")} /> : (
        <>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <Layers className="w-4 h-4" /><span data-testid="quiz-progress">{Object.keys(first).length} / {deckSize} done · {queue.length} left</span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gold-400 transition-[width]" style={{ width: `${deckSize ? (100 * Object.keys(first).length) / deckSize : 0}%` }} /></div>
            {c.tries > 0 && <span className="text-rose-600 font-semibold">Back again</span>}
          </div>
          <Card c={c} flipped={flipped} setFlipped={setFlipped} />
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="quiz-missed" onClick={() => answer(false)} className="inline-flex items-center justify-center gap-2 rounded-2xl border border-brand/70 bg-brand/15 py-4 font-bold text-rose-600 hover:bg-brand hover:text-white transition-colors"><X className="w-5 h-5" />Missed it</button>
            <button data-testid="quiz-got-it" onClick={() => answer(true)} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gold-400 py-4 font-bold text-black hover:bg-gold-300 transition-colors"><Check className="w-5 h-5" />Got it</button>
          </div>
          <button data-testid="quiz-mark-open" onClick={() => setMark(true)} className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 py-3 text-sm font-semibold text-slate-700 hover:text-white hover:border-gold-400/60"><PenLine className="w-4 h-4" />Write a full answer and get it marked</button>
          <MarkDialog open={mark} onOpenChange={setMark} question={c.q} modelAnswer={c.a} />
        </>
      )}
    </main>
  );
}
